// src/components/BoardPack/pages/profile_summary_pages/JeddahProfileSummary_2.jsx
import React, { useState, useEffect, useRef } from 'react';
import LoadingComponent from '@/components/common/LoadingComponent';
import ErrorState from '@/components/common/ErrorStateComponent';
import { apiCall } from '@/api/apiCall';
import { useQueue } from '@/context/QueueContext';
import jeddhalogo from '@/assets/img/jeddha-logo.png'
import carpark1 from '@/assets/img/car-park-1.png'
import { AssetsPanel } from '../../components/shared/AssetsPanel';
import { jeddahFallbackData } from '../../constant/jeddahFallbackData';
import { toast } from 'sonner';
import { API_CALL_TIME } from '@/constants/constant';

const JeddahProfileSummaryComponent2 = ({ profileData, parkingBaysData, year, monthNumber, contract }) => {
  const [data, _setData] = useState(profileData);
  const dataRef = useRef(profileData);
  
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);

  // ─── All refs for uncontrolled inputs ───────────────────────────────────
  // 🔥 REMOVED: totalBaysRef (ab readonly hai)
  const parkingBayRefs = useRef([]);
  const parkingRateRefs = useRef([]);
  const membershipRefs = useRef([]);
  const assetSubCountRefs = useRef([]);
  const assetTotalCountRefs = useRef([]);
  
  // Contract info refs
  const contractDurationRef = useRef(null);
  const contractStartDateRef = useRef(null);
  const contractEndDateRef = useRef(null);
  const contractOperationalHoursRef = useRef(null);
  const contractVatNoteRef = useRef(null);

  // 🔥 Use parkingBaysData ka total_bays (READ ONLY - second API se aaya hai)
  const effectiveTotalBays = parkingBaysData?.total_bays ?? profileData?.totalBays ?? 0;

  const setData = (valOrFn) => {
    _setData(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      dataRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    if (profileData) {
      _setData(profileData);
      dataRef.current = profileData;
    }
  }, [profileData]);

  const {
    airportName = ' ',
    airportNameTitle = ' ',
    contractInfo = {},
    parkingBays = [],
    parkingRates = [],
    membershipCharges = [],
    assetsDeployed = [],
  } = data || {};

  const autoSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveToAPI();
    }, API_CALL_TIME);
  };

  // ─── Read refs → build API payload ─────────────────────────────────────────
  const transformToApiFormat = () => {
    const d = dataRef.current;
    
    // 🔥 totalBays - ab read-only value use karo, input se nahi
    const currentTotalBays = effectiveTotalBays;
    const currentContractDuration = contractDurationRef.current?.value ?? contractInfo.duration;
    const currentContractStartDate = contractStartDateRef.current?.value ?? contractInfo.startDate;
    const currentContractEndDate = contractEndDateRef.current?.value ?? contractInfo.endDate;
    const currentContractOperationalHours = contractOperationalHoursRef.current?.value ?? contractInfo.operationalHours;
    const currentContractVatNote = contractVatNoteRef.current?.value ?? contractInfo.vatNote;

    return {
      contract: contract,
      year: year,
      month: monthNumber,
      created_by: 1,
      tables: [
        {
          table_key: "jeddah_airport_profile",
          title: "Jeddah Airport Profile",
          data: {
            car_park_bays: {
              label: "Jeddah Airport Car Park Bays",
              value: currentTotalBays
            },
            contract_term: {
              label: "Contract Term",
              value: currentContractDuration || ""
            },
            contract_period: {
              start_date: currentContractStartDate || "",
              end_date: currentContractEndDate || ""
            },
            operational_hours: {
              label: "Operational Hours",
              value: currentContractOperationalHours || ""
            },
            vat_note: {
              label: "Parking charges VAT",
              value: currentContractVatNote || ""
            }
          }
        },
        {
          table_key: "jeddah_airport_parking_bays_breakdown",
          title: "Jeddah Airport Parking Bays Breakdown",
          data: {
            columns: [
              "JEDDAH AIRPORT PARKING BAYS",
              String(currentTotalBays).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            ],
            rows: parkingBays.map((bay, i) => [
              bay.name,
              parkingBayRefs.current[i]?.value ?? bay.count
            ])
          }
        },
        {
          table_key: "jeddah_airport_parking_rates",
          title: "Parking Rates (Excluding VAT 15%)",
          data: {
            columns: [
              { column_key: "location", label: "Location", type: "text" },
              {
                column_key: "rates",
                label: "Rates",
                type: "group",
                sub_columns: [
                  { column_key: "pay_at_exit", label: "Pay at Exit", type: "text" },
                  { column_key: "pay_at_apm", label: "Pay at APM", type: "text" }
                ]
              },
              { column_key: "grace_period", label: "Grace Period After Payment", type: "text" }
            ],
            rows: parkingRates.map((rate, i) => ({
              location: rate.location,
              rates: {
                pay_at_exit: parkingRateRefs.current[i]?.payAtExit?.value ?? rate.payAtExit ?? "",
                pay_at_apm: parkingRateRefs.current[i]?.payAtAPM?.value ?? rate.payAtAPM ?? ""
              },
              grace_period: parkingRateRefs.current[i]?.gracePeriod?.value ?? rate.gracePeriod ?? ""
            }))
          }
        },
        {
          table_key: "jeddah_airport_membership_charges",
          title: "Membership Charges (Excluding VAT 15%)",
          data: {
            columns: ["Location", "Government Employee", "Commercial Employee"],
            rows: membershipCharges.map((charge, i) => [
              charge.location,
              membershipRefs.current[i]?.government?.value ?? charge.government ?? "",
              membershipRefs.current[i]?.commercial?.value ?? charge.commercial ?? ""
            ])
          }
        },
        {
          table_key: "jeddah_airport_assets_deployed",
          title: "Jeddah Airport",
          data: {
            groups: transformAssetsToApiFormat()
          }
        }
      ]
    };
  };

  const transformAssetsToApiFormat = () => {
    const assets = dataRef.current.assetsDeployed;
    const groups = [];
    let currentGroup = null;
    let subIndex = 0;

    assets.forEach((asset, i) => {
      if (asset.isCategory) {
        if (currentGroup) groups.push(currentGroup);
        const totalVal = assetTotalCountRefs.current[i]?.value;
        currentGroup = {
          group_name: asset.name,
          group_total_qty: totalVal !== undefined ? totalVal : (asset.totalCount || "-"),
          items: []
        };
        subIndex = 0;
      } else if (currentGroup) {
        const rawQty = assetSubCountRefs.current[i]?.value;
        const qty = rawQty !== undefined ? rawQty : (asset.subCount === '-' || !asset.subCount ? "-" : asset.subCount);
        currentGroup.items.push({
          item_name: asset.name,
          qty: isNaN(qty) ? "-" : qty
        });
        subIndex++;
      }
    });

    if (currentGroup) groups.push(currentGroup);
    return groups;
  };

  const saveToAPI = async () => {
    try {
      setIsSaving(true);
      const apiData = transformToApiFormat();
      const response = await apiCall("POST", "/boardpack/add-tables-data", apiData);
      if (response?.success) {
        // console.log("Data saved successfully");
      } else {
        throw new Error(response?.message || "Failed to update data");
      }
    } catch (err) {
      console.error("Error saving data:", err);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const editableClass = "w-full border-0 border-b border-gray-300 focus:border-blue-500 px-1 bg-transparent transition-colors outline-none";
  const readOnlyClass = "w-full border-0 bg-transparent px-1 text-gray-700 text-right font-medium";

  return (
    <div className="min-h-screen p-8">
      {/* Header */}
      <div className="text-center mb-2 py-0.5 flex justify-between items-center">
        <div className='ml-2'>
          <img src={jeddhalogo} alt="Logo" style={{ width: '100%', height: '50px', objectFit: 'cover' }} />
        </div>
        <h1 className="text-2xl font-bold leading-tight flex-1 text-center">
          {airportName}
        </h1>
      </div>

      <div className="flex flex-row gap-8 !border-2 p-3">
        {/* Left Column */}
        <div className="flex-1">
          {/* Top Info Block — total bays READ ONLY */}
          <div className="p-3 mb-3 w-auto">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-[34px] h-[34px] flex items-center justify-center">
                <img src={carpark1} alt="Car Park" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-row items-center">
                <span className="text-[14px] text-gray-700">{airportName} Car Park Bays</span>
                <span className="ml-1 text-[14px] font-bold">
                  {/* 🔥 READ-ONLY - No onChange, no ref, just display */}
                  <input
                    type="text"
                    value={effectiveTotalBays}
                    readOnly
                    disabled
                    className="text-[14px] font-bold w-20 text-left border-0 bg-transparent text-gray-900 outline-none cursor-default"
                  />
                </span>
              </div>
            </div>
            <div className="space-y-1 ml-4 text-xs leading-tight">
              <div className="flex items-start">
                <div className="mr-1 mt-0.5 w-2 h-2 !border !border-blue-600 flex-shrink-0" />
                <span className='inline-flex items-center gap-1.5 flex-wrap'>
                  Contract term 
                  <strong>
                    <input
                      type="text"
                      defaultValue={contractInfo.duration || ''}
                      ref={contractDurationRef}
                      onChange={() => autoSave()}
                      className="max-w-[120px] border-0 border-b border-gray-300 focus:border-blue-500 outline-none"
                    />
                  </strong> 
                  start date 
                  <strong>
                    <input
                      type="text"
                      defaultValue={contractInfo.startDate || ''}
                      ref={contractStartDateRef}
                      onChange={() => autoSave()}
                      className="max-w-[70px] border-0 border-b border-gray-300 focus:border-blue-500 outline-none"
                      placeholder="06/01/2021"
                    />
                  </strong> 
                  to 
                  <strong>
                    <input
                      type="text"
                      defaultValue={contractInfo.endDate || ''}
                      ref={contractEndDateRef}
                      onChange={() => autoSave()}
                      className="max-w-[80px] border-0 border-b border-gray-300 focus:border-blue-500 outline-none"
                      placeholder="31/07/2025"
                    />
                  </strong>
                </span>
              </div>
              <div className="flex items-start">
                <div className="mr-1 mt-0.5 w-2 h-2 !border !border-blue-600 flex-shrink-0" />
                <span>
                  Operational Hours 
                  <strong>
                    <input
                      type="text"
                      defaultValue={contractInfo.operationalHours || ''}
                      ref={contractOperationalHoursRef}
                      onChange={() => autoSave()}
                      className="inline-block ml-1 w-auto border-0 border-b border-gray-300 focus:border-blue-500 outline-none"
                    />
                  </strong>
                </span>
              </div>
              <div className="flex items-start">
                <div className="mr-1 mt-0.5 w-2 h-2 !border !border-blue-600 flex-shrink-0" />
                <span>
                  <input
                    type="text"
                    defaultValue={contractInfo.vatNote || ''}
                    ref={contractVatNoteRef}
                    onChange={() => autoSave()}
                    className="inline-block w-100 border-0 border-b border-gray-300 focus:border-blue-500 outline-none"
                  />
                </span>
              </div>
              <div className="flex items-center">
                <div className="mr-1 w-3 h-3 rounded-full !border !border-black flex-shrink-0 bg-red-500" />
                <span>Assets Deployed</span>
              </div>
            </div>
          </div>

          {/* Parking Bays Table - Total READ ONLY, individual bays EDITABLE */}
          <div className="flex flex-row gap-4 mb-6">
            <div className="flex-1">
              <div>
                <table className="w-full min-w-[300px] !border-collapse text-[9px]">
                  <tbody>
                    <tr className="bg-gray-300 font-bold">
                      <td className="p-1 font-black !border-1 text-black">
                        {airportName} PARKING BAYS
                      </td>
                      <td className="p-1 text-right !border-1 text-black">
                        {/* 🔥 READ-ONLY total bays display */}
                        {effectiveTotalBays.toLocaleString()}
                      </td>
                    </tr>
                    {parkingBays.map((bay, index) => (
                      <tr key={index}>
                        <td className="p-1 !border-1">
                          <span className="px-1">{bay.name}</span>
                        </td>
                        <td className="p-1 text-right !border-1">
                          <input
                            type="text"
                            defaultValue={bay.count}
                            ref={(el) => (parkingBayRefs.current[index] = el)}
                            onChange={() => autoSave()}
                            className={`${editableClass} text-right`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className=" block flex-1"></div>
          </div>

          {/* Bottom Tables - Editable */}
          <div className="flex flex-row gap-4 mb-4">
            {/* PARKING RATES */}
            <div className="w-1/2 h-full flex flex-col">
              <div className="!border-t-1 !border-l-1 !border-r-1 bg-gray-300 text-black px-2 py-1 text-[10px] font-bold">
                PARKING RATES EXCLUDING VAT 15%
              </div>
              <table className="w-full !border-collapse text-[11px]">
                <thead>
                  <tr>
                    <th rowSpan={2} className="p-1.5 text-center !border-1">LOCATION</th>
                    <th colSpan={2} className="p-1.5 text-center !border-1">RATES</th>
                    <th rowSpan={2} className="p-1.5 text-center !border-1">Grace Period after Payment</th>
                  </tr>
                  <tr>
                    <th className="p-1.5 text-left !border-1">Pay at Exit</th>
                    <th className="p-1.5 text-left !border-1">Pay at APM</th>
                  </tr>
                </thead>
                <tbody>
                  {parkingRates.map((rate, index) => {
                    if (!parkingRateRefs.current[index]) {
                      parkingRateRefs.current[index] = {};
                    }
                    return (
                      <tr key={index}>
                        <td className="p-1.5 !border-1 text-center">
                          <span className="px-1">{rate.location}</span>
                        </td>
                        <td className="p-1.5 !border-1 text-center">
                          <textarea
                            defaultValue={rate.payAtExit}
                            ref={(el) => {
                              parkingRateRefs.current[index] = {
                                ...parkingRateRefs.current[index],
                                payAtExit: el
                              };
                              if (el) {
                                el.style.height = "auto";
                                el.style.height = el.scrollHeight + "px";
                              }
                            }}
                            onChange={() => autoSave()}
                            className={`${editableClass} text-left resize-none overflow-hidden`}
                            rows={1}
                            onInput={(e) => {
                              e.target.style.height = "auto";
                              e.target.style.height = e.target.scrollHeight + "px";
                            }}
                          />
                        </td>
                        <td className="p-1.5 !border-1">
                          <input
                            type="text"
                            defaultValue={rate.payAtAPM}
                            ref={(el) => {
                              parkingRateRefs.current[index] = {
                                ...parkingRateRefs.current[index],
                                payAtAPM: el
                              };
                            }}
                            onChange={() => autoSave()}
                            className={`${editableClass} text-left mr-2`}
                          />
                        </td>
                        <td className="p-1.5 !border-1">
                          <input
                            type="text"
                            defaultValue={rate.gracePeriod}
                            ref={(el) => {
                              parkingRateRefs.current[index] = {
                                ...parkingRateRefs.current[index],
                                gracePeriod: el
                              };
                            }}
                            onChange={() => autoSave()}
                            className={`${editableClass} text-left mr-2`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MEMBERSHIP CHARGES */}
            <div className="w-1/2 h-full flex flex-col">
              <div className="!border-t-1 !border-l-1 !border-r-1 bg-gray-300 text-black px-2 py-1.5 text-[10px] font-bold">
                MEMBERSHIP CHARGES EXCLUDING VAT 15%
              </div>
              <table className="w-full !border-collapse text-[11px]">
                <thead>
                  <tr>
                    <th className="p-1.5 py-5 text-left !border-1">LOCATION</th>
                    <th className="p-1.5 py-5 text-left !border-1">GOVERNMENT EMPLOYEE</th>
                    <th className="p-1.5 py-5 text-left !border-1">COMMERCIAL EMPLOYEE</th>
                  </tr>
                </thead>
                <tbody>
                  {membershipCharges.map((charge, index) => (
                    <tr key={index}>
                      <td className="p-1.5 !border-1">
                        <span className="px-1">{charge.location}</span>
                      </td>
                      <td className="p-1.5 !border-1">
                        <input
                          type="text"
                          defaultValue={charge.government}
                          ref={(el) => {
                            if (!membershipRefs.current[index]) membershipRefs.current[index] = {};
                            membershipRefs.current[index].government = el;
                          }}
                          onChange={() => autoSave()}
                          className={editableClass}
                        />
                      </td>
                      <td className="p-1.5 !border-1">
                        <input
                          type="text"
                          defaultValue={charge.commercial}
                          ref={(el) => {
                            if (!membershipRefs.current[index]) membershipRefs.current[index] = {};
                            membershipRefs.current[index].commercial = el;
                          }}
                          onChange={() => autoSave()}
                          className={editableClass}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Side — Assets Deployed */}
        <div className="flex items-start m-0 p-0">
          <AssetsPanel
            assetsTable={{ title: airportNameTitle }}
            flatAssets={data.assetsDeployed}
            onSubCount={(index, value) => {
              setData(prev => {
                const updatedAssets = [...prev.assetsDeployed];
                updatedAssets[index] = { ...updatedAssets[index], subCount: value };
                return { ...prev, assetsDeployed: updatedAssets };
              });
              autoSave();
            }}
            onTotalCount={(index, value) => {
              setData(prev => {
                const updatedAssets = [...prev.assetsDeployed];
                updatedAssets[index] = { ...updatedAssets[index], totalCount: value };
                return { ...prev, assetsDeployed: updatedAssets };
              });
              autoSave();
            }}
            subCountRefs={assetSubCountRefs}
            totalCountRefs={assetTotalCountRefs}
          />
        </div>
      </div>
    </div>
  );
};

// Data fetching wrapper with TWO APIs
const getByKey = (apiData, key) => {
  const found = apiData.find(t => t.table_key === key);
  if (found) return found;
  return jeddahFallbackData.find(t => t.table_key === key);
};

const JeddahProfileSummary2 = ({ year = "", month = "", monthNumber = "", contract = "", page = null }) => {
  const { enqueue } = useQueue();
  const [profileData, setProfileData] = useState(null);
  const [parkingBaysData, setParkingBaysData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 🔥 Second API params - 'type' not 'value'
        const secondApiParams = {
          contract: "0020016",
          year: year,
          month: month,     // using 'month' (string like "Oct") not monthNumber
          type: "airport"
        };
        
        const [firstApiResponse, secondApiResponse] = await Promise.all([
          enqueue(() => apiCall(
            "GET", "/boardpack/get-tables-data",
            { year, contract, month: monthNumber }
          )),
          enqueue(() => apiCall(
            "GET", "/boardpack/parking/bays",
            secondApiParams
          ))
        ]);

        // Process first API response
        let firstData = firstApiResponse?.data || [];
        if (!Array.isArray(firstData) || firstData.length === 0) {
          firstData = jeddahFallbackData;
        }
        setProfileData(transformApiDataToComponentFormat(firstData));

        // Process second API response
        if (secondApiResponse?.success && secondApiResponse?.data) {
          setParkingBaysData(secondApiResponse.data);
        } else {
          console.warn("Second API failed:", secondApiResponse?.message);
          setParkingBaysData(null);
        }
        
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [year, month, monthNumber, contract, enqueue]);

  const transformApiDataToComponentFormat = (apiData) => {
    const title_main = getByKey(apiData, "jeddah_airport_profile") || "";
    const profileSummary = getByKey(apiData, "jeddah_airport_profile")?.data || {};
    const parkingBaysTable = getByKey(apiData, "jeddah_airport_parking_bays_breakdown")?.data || {};
    const parkingRatesTable = getByKey(apiData, "jeddah_airport_parking_rates")?.data || {};
    const membershipChargesTable = getByKey(apiData, "jeddah_airport_membership_charges")?.data || {};
    const assetsDeployedTable = getByKey(apiData, "jeddah_airport_assets_deployed")?.data || {};
    const assetsDeployedTableTitle = getByKey(apiData, "jeddah_airport_assets_deployed") || "";

    return {
      airportName: title_main?.title,
      airportNameTitle: assetsDeployedTableTitle?.title,
      totalBays: profileSummary.car_park_bays?.value || 0,
      contractInfo: {
        duration: profileSummary.contract_term?.value || '',
        startDate: profileSummary.contract_period?.start_date || '',
        endDate: profileSummary.contract_period?.end_date || '',
        operationalHours: profileSummary.operational_hours?.value || '',
        vatNote: profileSummary.vat_note?.value || ''
      },
      parkingBays: (parkingBaysTable.rows || []).map(row => ({
        name: row[0], count: row[1]
      })),
      parkingRates: (parkingRatesTable.rows || []).map(row => ({
        location: row.location,
        payAtExit: row.rates?.pay_at_exit || '',
        payAtAPM: row.rates?.pay_at_apm || '',
        gracePeriod: row.grace_period || ''
      })),
      membershipCharges: (membershipChargesTable.rows || []).map(row => ({
        location: row[0],
        government: row[1],
        commercial: row[2]
      })),
      assetsDeployed: transformAssetsDeployed(assetsDeployedTable.groups || []),
    };
  };

  const transformAssetsDeployed = (groups) => {
    const result = [];
    groups.forEach(group => {
      result.push({
        name: group.group_name || group.group || 'Unknown',
        isCategory: true,
        isBold: true,
        subCount: '',
        totalCount: group.group_total_qty != null ? String(group.group_total_qty) : ''
      });
      (group.items || []).forEach(item => {
        result.push({
          name: item.item_name || item.name || 'Unknown Item',
          isCategory: false,
          subCount: item.qty != null ? String(item.qty) : '-',
          totalCount: ''
        });
      });
    });
    return result;
  };

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} />;

  return (
    <JeddahProfileSummaryComponent2
      profileData={profileData}
      parkingBaysData={parkingBaysData}
      year={year}
      monthNumber={monthNumber}
      contract={contract}
    />
  );
};

export default JeddahProfileSummary2;